// Stockfish running in the browser (a WebAssembly build in a Web Worker), used for "Play the computer".
// The files live in public/engine (see the README there). Nothing is sent to our server, so it costs us nothing.
const ENGINE_URL = `${process.env.PUBLIC_URL || ""}/engine/stockfish.js`;

// The computer's levels. `skill` is Stockfish's own "Skill Level" (0-20), `depth` and `movetime` limit how far it
// looks ahead, and `randomness` is the chance that the lowest levels play a random legal move instead.
export const LEVELS = [
    {level: 1, name: "Beginner", blurb: "Just learning the moves", skill: 0, depth: 1, movetime: 40, randomness: 0.35},
    {level: 2, name: "Novice", blurb: "Knows the rules, misses a lot", skill: 1, depth: 2, movetime: 60, randomness: 0.15},
    {level: 3, name: "Casual", blurb: "A relaxed, friendly game", skill: 3, depth: 3, movetime: 100, randomness: 0.05},
    {level: 4, name: "Club player", blurb: "Plays solid, sometimes slips", skill: 6, depth: 5, movetime: 150, randomness: 0},
    {level: 5, name: "Strong", blurb: "Punishes most mistakes", skill: 10, depth: 8, movetime: 250, randomness: 0},
    {level: 6, name: "Expert", blurb: "Hard to beat", skill: 14, depth: 12, movetime: 500, randomness: 0},
    {level: 7, name: "Master", blurb: "Very hard to beat", skill: 17, depth: 16, movetime: 800, randomness: 0},
    {level: 8, name: "Grandmaster", blurb: "Full strength, good luck", skill: 20, depth: 22, movetime: 1200, randomness: 0}
];

export const levelInfo = (level) => LEVELS.find((l) => l.level === Number(level)) || LEVELS[2];

// Loading the engine means downloading and compiling about 7 MB, which can take a while on a slow connection
const START_TIMEOUT_MS = 90000;
// How long a search may run past its own time limit before we nudge it with "stop"
const SEARCH_GRACE_MS = 8000;
// How long we wait for the answer after "stop" before giving up on that worker and starting a fresh one
const STOP_GRACE_MS = 3000;

const engineError = (message, extra = {}) => Object.assign(new Error(message), extra);

export const createEngine = () => {
    let worker = null;
    let ready = null;
    let stopped = false;
    // Everything we are waiting for, so it can be cancelled when the worker dies or the engine is stopped
    const waits = new Set();

    const send = (command) => worker && worker.postMessage(command);

    const waitFor = (matches, timeoutMs) => new Promise((resolve, reject) => {
        const wait = {
            matches,
            resolve: (line) => {
                clearTimeout(wait.timer);
                waits.delete(wait);
                resolve(line);
            },
            reject: (error) => {
                clearTimeout(wait.timer);
                waits.delete(wait);
                reject(error);
            }
        };
        wait.timer = setTimeout(() => wait.reject(engineError("The chess engine did not answer in time.", {timeout: true})), timeoutMs);
        waits.add(wait);
    });

    const rejectAll = (error) => [...waits].forEach((wait) => wait.reject(error));

    // Throws away the current worker. The next search starts a fresh one.
    const kill = (error) => {
        if (worker) {
            worker.onmessage = null;
            worker.onerror = null;
            worker.terminate();
        }
        worker = null;
        ready = null;
        rejectAll(error);
    };

    const start = () => {
        if (stopped) return Promise.reject(engineError("The chess engine was stopped.", {stopped: true}));
        if (ready) return ready;
        const starting = (async () => {
            worker = new Worker(ENGINE_URL);
            worker.onmessage = (event) => {
                const text = typeof event.data === "string" ? event.data : String(event.data);
                text.split("\n").forEach((raw) => {
                    const line = raw.trim();
                    [...waits].forEach((wait) => wait.matches(line) && wait.resolve(line));
                });
            };
            // The worker can crash at any time, not only while starting. Then everything waiting fails right away.
            worker.onerror = (event) => {
                if (event && event.preventDefault) event.preventDefault();
                kill(engineError("The chess engine stopped working.", {crashed: true}));
            };
            const uciOk = waitFor((line) => line === "uciok", START_TIMEOUT_MS);
            send("uci");
            await uciOk;
            const readyOk = waitFor((line) => line === "readyok", START_TIMEOUT_MS);
            send("isready");
            await readyOk;
        })();
        ready = starting;
        starting.catch((error) => {
            // Only forget a start that failed if it is still the current one
            if (ready === starting) kill(error);
        });
        return starting;
    };

    // `position` is either a FEN, or {startFen, moves} with the moves played so far in UCI notation. Giving the moves
    // matters: from a bare FEN Stockfish cannot know which positions already happened, so it would walk into a draw by
    // repetition even when it is winning.
    const positionCommand = (position) => {
        if (typeof position === "string") return `position fen ${position}`;
        const moves = position.moves && position.moves.length ? ` moves ${position.moves.join(" ")}` : "";
        return `position fen ${position.startFen}${moves}`;
    };

    const search = async (position, {skill, depth, movetime}) => {
        await start();
        send(`setoption name Skill Level value ${skill}`);
        send("setoption name MultiPV value 1");
        send(positionCommand(position));
        const answer = waitFor((line) => line.startsWith("bestmove"), movetime + SEARCH_GRACE_MS);
        send(`go depth ${depth} movetime ${movetime}`);
        let line;
        try {
            line = await answer;
        } catch (error) {
            if (!error.timeout) throw error;
            // Taking too long (for example when the browser paused the tab): ask for its best move so far
            const afterStop = waitFor((l) => l.startsWith("bestmove"), STOP_GRACE_MS);
            send("stop");
            line = await afterStop;
        }
        const move = line.split(/\s+/)[1];
        return !move || move === "(none)" ? null : move;
    };

    // Full-strength look at a position. Returns the best line: the evaluation (from the side to move, in centipawns
    // or as mate in N) and the moves in UCI notation, plus `lines` with the `multipv` best lines. Used for the
    // evaluation bar, the best-move arrow and Rook's briefing.
    const analysePosition = async (position, {depth = 16, movetime = 800, multipv = 1} = {}) => {
        await start();
        send("setoption name Skill Level value 20");
        send(`setoption name MultiPV value ${multipv}`);
        send(positionCommand(position));
        const lines = [];
        let noMoves = null;
        const answer = waitFor((line) => {
            if (line.startsWith("info") && line.includes(" pv ") && !line.includes("bound")) {
                const cp = line.match(/score cp (-?\d+)/);
                const mate = line.match(/score mate (-?\d+)/);
                const index = Number((line.match(/ multipv (\d+)/) || [])[1] || 1) - 1;
                lines[index] = {
                    cp: cp ? Number(cp[1]) : null,
                    mate: mate ? Number(mate[1]) : null,
                    pv: line.split(" pv ")[1].trim().split(/\s+/)
                };
            } else if (line.startsWith("info depth 0")) {
                // No legal moves: checkmate (mate 0) or stalemate (cp 0)
                const mate = line.match(/score mate (-?\d+)/);
                noMoves = {cp: mate ? null : 0, mate: mate ? 0 : null, pv: []};
            }
            return line.startsWith("bestmove");
        }, movetime + SEARCH_GRACE_MS);
        send(`go depth ${depth} movetime ${movetime}`);
        try {
            await answer;
        } catch (error) {
            if (!error.timeout) throw error;
            const afterStop = waitFor((l) => l.startsWith("bestmove"), STOP_GRACE_MS);
            send("stop");
            await afterStop;
        }
        const found = lines.filter(Boolean);
        const best = noMoves || found[0] || {cp: 0, mate: null, pv: []};
        return {...best, lines: noMoves ? [] : found};
    };

    // Searches run one at a time: each waits for its own "bestmove" before the next one starts
    let queue = Promise.resolve();

    const analyse = (position, options) => {
        const run = queue.then(async () => {
            try {
                return await analysePosition(position, options);
            } catch (error) {
                if (error.stopped) throw error;
                kill(engineError("The chess engine is restarting.", {restarting: true}));
                return analysePosition(position, options);
            }
        });
        queue = run.catch(() => {});
        return run;
    };

    // Returns the engine's move in UCI notation (e.g. "e2e4" or "e7e8q"), or null if there is none.
    // If the engine hangs or crashes, it is restarted once and asked again before we give up.
    const bestMove = (position, options) => {
        const run = queue.then(async () => {
            try {
                return await search(position, options);
            } catch (error) {
                if (error.stopped) throw error;
                kill(engineError("The chess engine is restarting.", {restarting: true}));
                return search(position, options);
            }
        });
        queue = run.catch(() => {});
        return run;
    };

    const stop = () => {
        stopped = true;
        kill(engineError("The chess engine was stopped.", {stopped: true}));
    };

    return {start, bestMove, analyse, stop};
};
